import React, { useState, useEffect } from 'react';
import PageTransition from '../../components/PageTransition';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiMail, FiShield, FiUser, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, TABLES } from '../../supabase/supabase';

const Team = () => {
  const { activeOrg } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'researcher', access: 'full' });

  useEffect(() => {
    if (activeOrg) fetchMembers();
  }, [activeOrg]);

  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.ORG_MEMBERS)
        .select(`id, role, status, created_at, user_id`)
        .eq('org_id', activeOrg.id);

      if (error) throw error;
      setMembers(data || []);
    } catch (err) {
      console.error("Error fetching team:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    alert(`Invite sent to ${inviteData.email}!`);
    setShowInviteModal(false);
  };

  const inputClasses = "w-full px-5 py-3.5 bg-white border border-gray-200 rounded-md focus:border-rutabaga-pink focus:ring-2 focus:ring-rutabaga-pink/10 outline-none transition-all font-medium text-rutabaga-dark";

  return (
    <PageTransition className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Team Members</h1>
          <p className="text-gray-500 mt-1 font-medium italic">Manage who has access to the {activeOrg?.name} workspace.</p>
        </div>
        <button 
          onClick={() => setShowInviteModal(true)}
          className="rutabaga-button-primary flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} /> Invite Member
        </button>
      </div>

      <div className="bg-white rounded-md border border-gray-100 shadow-xl shadow-indigo-500/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Member</th>
                <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Role</th>
                <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Status</th>
                <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {members.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-50 rounded flex items-center justify-center text-gray-400 font-bold">
                        <SafeIcon icon={FiUser} />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">Team Member</p>
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-tight">ID: {member.user_id?.slice(0,8)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiShield} className={member.role === 'admin' ? 'text-amber-500' : 'text-gray-400'} />
                      <span className="text-xs font-bold text-gray-700 capitalize">{member.role}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${member.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {member.status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <button className="text-gray-300 hover:text-rutabaga-pink transition-colors p-2 rounded-md hover:bg-red-50">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-rutabaga-dark/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-lg p-10 shadow-2xl animate-in fade-in zoom-in duration-200 border-t-4 border-rutabaga-pink">
            <h2 className="text-3xl font-black text-gray-900 mb-2">Invite Team Member</h2>
            <p className="text-gray-500 mb-8 font-medium">Send an email invitation to join your workspace.</p>
            
            <form onSubmit={handleInvite} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type="email" 
                    required
                    className={`${inputClasses} pl-11`}
                    placeholder="teammate@example.com"
                    value={inviteData.email}
                    onChange={e => setInviteData({...inviteData, email: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Role</label>
                  <select 
                    className={`${inputClasses} py-3`}
                    value={inviteData.role}
                    onChange={e => setInviteData({...inviteData, role: e.target.value})}
                  >
                    <option value="researcher">Researcher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Access</label>
                  <select 
                    className={`${inputClasses} py-3`}
                    value={inviteData.access}
                    onChange={e => setInviteData({...inviteData, access: e.target.value})}
                  >
                    <option value="full">Full Access</option>
                    <option value="project">Project Only</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4 items-center">
                <button 
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-500 hover:text-gray-700 font-medium px-4"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 rutabaga-button-primary"
                >
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageTransition>
  );
};

export default Team;